import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';

import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SupportIcon from '@mui/icons-material/Help';
import PaymentIcon from '@mui/icons-material/Payment';
import SettingsIcon from '@mui/icons-material/Settings';
import { mrLogo } from '../../../assets/branding';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import SideNavSkeleton from './SideNavSkeleton';

interface SideNavProps {
  width: number;
  onClose?: () => void;
}

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

const SideNav: React.FC<SideNavProps> = ({ width, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoadingProfile } = useAuth();
  const theme = useTheme();
  const mainNavItems: NavItem[] = [
    { text: 'Home', icon: <HomeIcon />, path: '/home' },
    // { text: 'Resources', icon: <LibraryBooksIcon />, path: '/resources' },
  ];

  const secondaryNavItems: NavItem[] = [
    { text: 'Billing', icon: <PaymentIcon />, path: '/billing' },
    { text: 'Account Settings', icon: <SettingsIcon />, path: '/account-settings' },
    { text: 'Questions? Email us!', icon: <SupportIcon />, path: '/support' },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  if (isLoadingProfile) {
    return <SideNavSkeleton />;
  }

  return (
    <Box
      component='nav'
      sx={{
        width: width,
        backgroundColor: '#FFFFFF', // Changed from #F8F9FC to match header
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '& *::-webkit-scrollbar': {
          width: 5,
        },
        '& *::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 5,
        },
        '& *::-webkit-scrollbar-thumb': {
          background: '#dadce0',
          borderRadius: 5,
        },
        '& *::-webkit-scrollbar-thumb:hover': {
          background: '#a0a0a0',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
          px: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <img src={mrLogo} alt='logo' style={{ width: 90 }} data-testid='sidenav-header-icon' />

        {onClose && (
          <IconButton edge='end' color='inherit' onClick={onClose} aria-label='close drawer'>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <List sx={{ py: 0 }}>
          {mainNavItems.map((item) => {
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 1.5,
                    pl: 2,
                    pr: 4,
                    color: isActive ? theme.palette.primary[400] : '#5F6368',
                    backgroundColor: isActive ? theme.palette.primary.contrastText : 'transparent',
                    borderTopRightRadius: 20,
                    borderBottomRightRadius: 20,
                    mr: 1,
                    '&:hover': {
                      backgroundColor: isActive ? '#EBF2FF' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isActive ? theme.palette.primary[400] : 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: isActive ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 1, mx: 2 }} />

        <List sx={{ py: 0 }}>
          {secondaryNavItems.map((item) => {
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 1.5,
                    pl: 2,
                    pr: 4,
                    color: isActive ? theme.palette.primary[400] : '#5F6368',
                    backgroundColor: isActive ? theme.palette.primary.contrastText : 'transparent',
                    borderTopRightRadius: 20,
                    borderBottomRightRadius: 20,
                    mr: 1,
                    '&:hover': {
                      backgroundColor: isActive ? theme.palette.primary.contrastText : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isActive ? theme.palette.primary[400] : 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    slotProps={{
                      primary: {
                        fontSize: 14,
                        fontWeight: isActive ? 600 : 400,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default SideNav;